/**
 * Logger abstraction layer
 *
 * This module provides a simple logging abstraction that can be
 * extended to support different logging backends through a minimal adapter system.
 */

// Log levels
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

// Logger adapter interface - implement this to add a new logging backend
export type LoggerAdapter = {
  debug(namespace: string, message: string, ...args: unknown[]): void;
  info(namespace: string, message: string, ...args: unknown[]): void;
  warn(namespace: string, message: string, ...args: unknown[]): void;
  error(namespace: string, message: string, ...args: unknown[]): void;
};

// Logger interface
export type Logger = {
  debug(message: string, ...args: unknown[]): void;
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
};

// Default console logger adapter
export class ConsoleLoggerAdapter implements LoggerAdapter {
  debug(namespace: string, message: string, ...args: unknown[]): void {
    console.debug(`[${namespace}] ${message}`, ...args);
  }

  info(namespace: string, message: string, ...args: unknown[]): void {
    console.info(`[${namespace}] ${message}`, ...args);
  }

  warn(namespace: string, message: string, ...args: unknown[]): void {
    console.warn(`[${namespace}] ${message}`, ...args);
  }

  error(namespace: string, message: string, ...args: unknown[]): void {
    console.error(`[${namespace}] ${message}`, ...args);
  }
}

// Global registry of logger adapters
const loggerAdapters: LoggerAdapter[] = [new ConsoleLoggerAdapter()];

// Add a new logger adapter
export function addLoggerAdapter(adapter: LoggerAdapter): void {
  loggerAdapters.push(adapter);
}

// Enhanced logger implementation that uses all registered adapters
class EnhancedLogger implements Logger {
  constructor(private readonly namespace: string) {}

  debug(message: string, ...args: unknown[]): void {
    for (const adapter of loggerAdapters) {
      try {
        adapter.debug(this.namespace, message, ...args);
      } catch (error) {
        // Prevent adapter errors from breaking the application
        console.error('Logger adapter error:', error);
      }
    }
  }

  info(message: string, ...args: unknown[]): void {
    for (const adapter of loggerAdapters) {
      try {
        adapter.info(this.namespace, message, ...args);
      } catch (error) {
        console.error('Logger adapter error:', error);
      }
    }
  }

  warn(message: string, ...args: unknown[]): void {
    for (const adapter of loggerAdapters) {
      try {
        adapter.warn(this.namespace, message, ...args);
      } catch (error) {
        console.error('Logger adapter error:', error);
      }
    }
  }

  error(message: string, ...args: unknown[]): void {
    for (const adapter of loggerAdapters) {
      try {
        adapter.error(this.namespace, message, ...args);
      } catch (error) {
        console.error('Logger adapter error:', error);
      }
    }
  }
}

// Create a logger for a specific namespace
export function createLogger(namespace: string): Logger {
  return new EnhancedLogger(namespace);
}

// Default logger
export const logger = createLogger('matic');

export default logger;
